import { riskTier, type Fixture } from '@h2h/shared';
import { ChevronRight, Lock, Plus } from '../../ui/icons';
import { PickPointsBadge } from '../PickPointsBadge';
import { cn } from '../../../lib/cn';
import { signedPoints } from '../../../lib/format';
import { pickOutcome } from '../../../lib/pickOutcome';
import { teamCode } from '../../../lib/teams';
import { TeamBadge } from '../../TeamBadge';
import {
  CARD_AT_RISK,
  draftMultiplier,
  draftToWin,
  formatMultiplier,
  isCardStarted,
  toCard,
  type DraftCard
} from '../../../lib/picks';
import { timeLabel } from '../../../screens/play/helpers';
import { TIER_COLOR, TIER_TEXT } from './tokens';
import { BasePickPill } from './BasePickCornerStamp';

/**
 * One fixture row in the round overview. Pre-kickoff it's a tap target for the card
 * builder (with a live "to win" footer once a card is started); once the match goes
 * LIVE or SETTLED it flips to a read-only summary that's tappable to open the live /
 * result match view (signalled by the chevron + "follow live" nudge).
 */
export function FixtureOverviewCard({
  fixture: f,
  card,
  now,
  flag,
  onOpen
}: {
  fixture: Fixture;
  card: DraftCard | undefined;
  now: number;
  flag: boolean;
  onOpen: (fixtureId: string) => void;
}) {
  const started = !!card && isCardStarted(card);
  const isLive = f.status === 'LIVE';
  const isSettled = f.status === 'SETTLED' || f.status === 'VOID';
  const kickoffLocked = isLive || isSettled || f.status !== 'SCHEDULED' || now >= f.kickoffAt;
  const editable = !kickoffLocked;
  const home = teamCode(f.homeTeam, f.homeCode);
  const away = teamCode(f.awayTeam, f.awayCode);
  const legCount = card?.legs.length ?? 0;
  const mult = card ? draftMultiplier(card) : 1;
  const tier = riskTier(mult);
  const maxed = legCount >= 5;
  const ringClass = flag ? 'ring-1 ring-inset ring-black/10' : 'border border-black/[0.08]';
  const prediction = started && card?.outcome ? toCard(card) : null;
  const outcome = prediction ? pickOutcome(prediction, f) : null;
  const showResult = !!outcome;

  const liveScore = isLive
    ? `${f.liveHomeGoals ?? 0}–${f.liveAwayGoals ?? 0}`
    : isSettled && f.result
      ? `${f.result.homeGoals}–${f.result.awayGoals}`
      : null;

  const timeInfo = isLive
    ? f.elapsed != null ? `${f.elapsed}'` : 'Live'
    : isSettled
      ? `${timeLabel(f.kickoffAt)} · FT`
      : timeLabel(f.kickoffAt);

  // Two-zone card: no padding on the button — sections carry their own.
  const cardShell = cn(
    'tap w-full overflow-hidden rounded-[20px] text-left transition-[transform,box-shadow] duration-150 ease-ios-out active:scale-[0.99]',
    isLive
      ? 'border border-[rgba(239,65,53,0.2)] shadow-[0_8px_22px_-10px_rgba(239,65,53,0.2)]'
      : started
        ? 'border border-black/[0.08] shadow-[0_8px_24px_-8px_rgba(20,20,50,0.16),0_2px_6px_-2px_rgba(20,20,50,0.06)]'
        : 'border border-dashed border-black/[0.18]',
    kickoffLocked && !isLive && !isSettled && 'cursor-not-allowed opacity-40'
  );

  const headerBg = isLive ? 'bg-white' : isSettled ? 'bg-[#F8F8FA]' : 'bg-white';

  const footerBg = showResult
    ? outcome!.perfect
      ? 'bg-[linear-gradient(135deg,rgba(46,219,123,0.12),rgba(11,160,120,0.05))] border-t border-[rgba(36,197,106,0.22)]'
      : outcome!.points > 0
        ? 'bg-[rgba(36,197,106,0.06)] border-t border-[rgba(36,197,106,0.16)]'
        : isLive
          ? 'bg-[rgba(239,65,53,0.04)] border-t border-[rgba(239,65,53,0.1)]'
          : 'bg-[#F2F2F5] border-t border-black/[0.05]'
    : isLive
      ? 'bg-[rgba(239,65,53,0.04)] border-t border-[rgba(239,65,53,0.1)]'
      : isSettled
        ? 'bg-[#F2F2F5] border-t border-black/[0.05]'
        : 'bg-[#F7F7FB] border-t border-[#EEEEF2]';

  return (
    <button
      type="button"
      disabled={kickoffLocked && !isLive && !isSettled}
      onClick={() => onOpen(f.fixtureId)}
      className={cardShell}
    >
      {/* ── Header zone: match identity + pick ─────────────────────── */}
      <div className={cn('px-[14px] py-[13px]', headerBg)}>
        {/* Live / Settled inline status strip */}
        {(isLive || isSettled) && (
          <div className={cn(
            'mb-[10px] flex items-center gap-[6px] rounded-[8px] px-2 py-[5px]',
            isLive ? 'bg-[rgba(239,65,53,0.08)]' : 'bg-[#EAEAEE]'
          )}>
            {isLive
              ? <span className="h-[5px] w-[5px] shrink-0 animate-h2h-pulse rounded-full bg-[#EF4135]" />
              : <Lock width={9} height={9} strokeWidth={2.6} className="shrink-0 text-ink3" />}
            <span className={cn(
              'flex-1 text-[10.5px] font-extrabold tracking-[0.02em]',
              isLive ? 'text-[#C0392B]' : 'text-ink3'
            )}>
              {isLive ? (f.elapsed != null ? `Live · ${f.elapsed}'` : 'Live now') : 'Full time'}
            </span>
            {liveScore && (
              <span className={cn('num font-extrabold tracking-[-0.02em]', isLive ? 'text-[13px] text-ink' : 'text-[12px] text-ink2')}>
                {liveScore}
              </span>
            )}
            <ChevronRight
              width={13}
              height={13}
              strokeWidth={2.6}
              className={cn('-mr-[2px] shrink-0', isLive ? 'text-[#C0392B]/55' : 'text-ink3/55')}
            />
          </div>
        )}

        <div className="flex items-center gap-[11px]">
          {/* Team badges */}
          <div className="flex shrink-0 items-center">
            <TeamBadge code={home} logo={f.homeLogo} name={f.homeTeam} flag={flag} sizeClass="h-[32px] w-[32px]" roundedClass="rounded-[9px]" textClass="text-[8px]" className={cn(ringClass, (isLive || isSettled) && 'opacity-70')} />
            <TeamBadge code={away} logo={f.awayLogo} name={f.awayTeam} flag={flag} sizeClass="h-[32px] w-[32px]" roundedClass="rounded-[9px]" textClass="text-[8px]" className={cn('-ml-[9px]', ringClass, (isLive || isSettled) && 'opacity-70')} />
          </div>

          {/* Team names + time */}
          <div className="min-w-0 flex-1">
            <div className={cn('truncate text-[14px] font-extrabold tracking-[-0.015em]', isSettled ? 'text-ink2' : 'text-ink')}>
              {f.homeTeam} <span className="font-semibold text-[#CACAD2]">v</span> {f.awayTeam}
            </div>
            <div className={cn('mt-[3px] text-[11.5px] font-semibold', isLive ? 'text-[#C0392B]' : 'text-ink3')}>
              {timeInfo}
            </div>
          </div>

          {/* Pick outcome or state chip */}
          {isLive || isSettled ? (
            started && card!.outcome ? (
              <BasePickPill outcome={card!.outcome} fixture={f} homeCode={home} awayCode={away} flag={flag} />
            ) : (
              <span className={cn(
                'inline-flex items-center gap-1 rounded-[8px] px-2 py-[5px] text-[10.5px] font-extrabold',
                isLive ? 'bg-[rgba(239,65,53,0.10)] text-[#C0392B]' : 'bg-[#E8E8EE] text-ink3'
              )}>
                {isLive ? 'Live' : 'FT'}
              </span>
            )
          ) : started ? (
            card!.outcome ? (
              <BasePickPill outcome={card!.outcome} fixture={f} homeCode={home} awayCode={away} flag={flag} />
            ) : (
              <span className="shrink-0 rounded-[9px] border border-dashed border-[#E3A82B]/50 bg-[rgba(227,168,43,0.08)] px-[10px] py-[6px] text-[10px] font-extrabold uppercase tracking-[0.04em] text-[#B5740A]">
                Pick base
              </span>
            )
          ) : (
            <span className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-full bg-night">
              <Plus width={15} height={15} strokeWidth={2.8} className="text-white" />
            </span>
          )}
        </div>
      </div>

      {/* ── Stats footer zone ────────────────────────────────────────── */}
      {started && (
        <div className={cn('px-[14px] py-[11px]', footerBg)}>
          {!card!.outcome && editable && (
            <div className="mb-[9px] rounded-[8px] border border-dashed border-[#E3A82B]/40 bg-[rgba(227,168,43,0.07)] px-3 py-[7px] text-center text-[11px] font-extrabold text-[#B5740A]">
              Choose your 1X2 base to lock this card in
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            {/* Left: boost chips on white pill backgrounds */}
            <div className="flex min-w-0 flex-nowrap items-center gap-[5px]">
              <span className={cn(
                'shrink-0 rounded-[7px] border px-[8px] py-[4px] text-[11px] font-bold',
                isSettled
                  ? 'border-black/[0.06] bg-[#EAEAEE] text-ink3'
                  : 'border-black/[0.07] bg-white text-ink shadow-[0_1px_2px_rgba(20,20,50,0.08)]'
              )}>
                {legCount} {legCount === 1 ? 'boost' : 'boosts'}
              </span>
              {legCount > 0 && (
                <span
                  className={cn('num shrink-0 rounded-[7px] px-[8px] py-[4px] text-[11px] font-extrabold', isSettled && 'opacity-55')}
                  style={{ background: TIER_COLOR[tier], color: TIER_TEXT[tier] }}
                >
                  ×{formatMultiplier(mult)}
                </span>
              )}
              {maxed && (
                <span className={cn('shrink-0 rounded-[7px] px-[7px] py-[4px] text-[10px] font-extrabold', isSettled ? 'bg-[#EAEAEE] text-ink3' : 'bg-[rgba(239,65,53,0.12)] text-[#EF4135]')}>
                  MAXED
                </span>
              )}
            </div>

            {/* Right: projected win/risk before kickoff, live or final score after */}
            {showResult ? (
              <PickPointsBadge outcome={outcome!} size="large" />
            ) : (
              <div className="flex shrink-0 items-center gap-[14px]">
                <div className="text-right">
                  <div className="num text-[15px] font-extrabold tracking-[-0.02em] text-[#EF4135]">
                    {signedPoints(CARD_AT_RISK)}
                  </div>
                  <div className="mt-[1px] text-[9px] font-bold uppercase tracking-[0.04em] text-ink3">risk</div>
                </div>
                <div className="h-[28px] w-px bg-black/[0.08]" />
                <div className="text-right">
                  <div className="num text-[22px] font-extrabold leading-none tracking-[-0.03em] text-[#0A9A72]">
                    {draftToWin(card!)}
                  </div>
                  <div className="mt-[2px] text-[9px] font-bold uppercase tracking-[0.04em] text-ink3">to win</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Empty card: small footer nudge */}
      {!started && !kickoffLocked && (
        <div className="border-t border-dashed border-black/[0.12] bg-[#FAFAFA] px-[14px] py-[10px]">
          <span className="text-[11px] font-semibold text-ink3">Tap to build a card</span>
        </div>
      )}

      {/* Live, no card built: invite the player to follow it live */}
      {isLive && !started && (
        <div className="border-t border-[rgba(239,65,53,0.12)] bg-[rgba(239,65,53,0.05)] px-[14px] py-[10px]">
          <span className="flex items-center gap-[5px] text-[11px] font-bold text-[#C0392B]">
            <span className="h-[5px] w-[5px] animate-h2h-pulse rounded-full bg-[#EF4135]" />
            Tap to follow this match live
          </span>
        </div>
      )}
    </button>
  );
}
